import colorConvert from 'color-convert';
import { useTranslation } from 'next-i18next';
import React, { useEffect, useState } from 'react';
import { usePermission } from '../../../../../../../hooks/usePermission';
import usePresentations from '../../../../../../../hooks/usePresentations';
import { Slot } from '../../../../../../../interfaces/interfaces';
import { useSelector } from '../../../../../../../reduxToolkit/typedRedux';
import { createDisplayLink } from '../../../../../../../services/helper';
import InlineEditButton from '../../../../../pageBuilder/InlineEditButton';
import PresentationEditComponent from './PresentationEditComponent';
import PublicPresentationLeftContent from './PublicPresentationLeftContent';
import PublicPresentationRightContent from './PublicPresentationRightContent';
import { usePresentationBooking } from '../../../../../../../hooks/usePresentationBooking';

interface PublicPresentationInterface {
  slot: Slot;
  boxClassName?: string;
  spotlight?: boolean;
  presentationTextColor?: string;
  showStartEndTime?: boolean;
}

/**
 * this component displays a single presentations card
 * @param slot the slot presentation to display
 * @param boxClassName classnames to append
 * @param favorite
 * @constructor
 */
const PublicPresentationComponent = ({
  slot,
  boxClassName,
  spotlight = false,
  presentationTextColor = '#FFFFFF',
  showStartEndTime = false,
}: PublicPresentationInterface) => {
  const eventColor = useSelector(({ common }) => common.event?.template.mainColor);
  const user = useSelector(({ common }) => common.user);
  const [, setColor] = useState([216, 97, 34]);
  const backgroundImg = calcBgImage();
  const { t } = useTranslation();
  const { isBoothHost } = usePermission();
  const { joinPresentation, getPlaceholderBackgroundImage } = usePresentations();
  const { canJoinPresentation } = usePresentationBooking();

  const globalEditMode = useSelector(({ common }) => common.templateEditMode);

  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (eventColor) {
      const [h, s, l] = colorConvert.hex.hsl(eventColor);
      setColor([h, s, l]);
    }
  }, [eventColor]);

  function calcBgImage() {
    const { presentationBackground, presentationSpotlight } = slot.meetingroom;
    const { backgroundImage } = slot.meetingroom.booth;
    const spotlightedProp = presentationSpotlight || presentationBackground || backgroundImage;
    const noSpotlightProp = presentationBackground || backgroundImage;
    return spotlight ? spotlightedProp : noSpotlightProp;
  }

  const isOnSite = slot.meetingroom.presentationSlotType === 'on-site';
  const isLiveStream = !!slot.meetingroom.liveStream;
  return (
    <div>
      <div
        className={`${((!isOnSite && canJoinPresentation(slot)) || isLiveStream) && 'cursor-pointer'} relative my-4 @md:mr-4 ${
          spotlight ? 'col-span-1 @lg:col-span-2' : ''
        }`}
        onClick={() => ((!isOnSite && !globalEditMode && canJoinPresentation(slot)) || isLiveStream) && joinPresentation(slot)}
        key={slot._id}
        id={slot._id}>
        <div
          className={`${boxClassName} transition-shadow w-full duration-200 templateBorder
          bg-cover bg-no-repeat bg-center flex justify-between min-w-[300px] min-h-[200px] overflow-hidden break-word whitespace-pre-line relative
        `}
          style={{
            backgroundImage: `linear-gradient(20deg, rgba(0, 0, 0, 0.4) 0%, rgba(150, 150, 150, 0.3) 60%, rgba(238, 238, 238, 0.2) 100%),
            url(${backgroundImg ? createDisplayLink(backgroundImg, false) : getPlaceholderBackgroundImage(slot.meetingroom)})`,
          }}>
          {globalEditMode && (
            <div className="absolute top-0 right-0">
              <InlineEditButton
                isEditMode={editing}
                setIsEditMode={() => {
                  setEditing(!editing);
                }}
                tooltip={t('account:edit-presentation')}
                forceShow={isBoothHost(user, [slot.meetingroom.booth])}
              />
            </div>
          )}
          {editing && globalEditMode ? (
            <PresentationEditComponent meetingroom={slot.meetingroom} />
          ) : (
            <>
              <PublicPresentationLeftContent slot={slot} presentationTextColor={presentationTextColor} />
              {!globalEditMode && <PublicPresentationRightContent slot={slot} joinRoom={() => joinPresentation(slot)} showStartEndTime={showStartEndTime} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicPresentationComponent;
